import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { WidgetSentryErrorHandler } from './widget-sentry';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpInterceptorFn, provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { feedbackApiAuthInterceptor, provideFeedbackApi } from '@brafton/feedback-api';
import { DisregardReasonDialog, StatusChip } from '@brafton/feedback-components';

import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { CaptureOptions } from './dialogs/captureOptions/captureOptions.component';
import { CaptureScreenshot } from './dialogs/capture-screenshot/capture-screenshot';
import { IdentityModal } from './dialogs/identity-modal/identity-modal';
import { NewFeedback } from './dialogs/new-feedback/new-feedback';
import { FeedbackDetail } from './dialogs/feedback-detail/feedback-detail';
import { MockupViewer } from './dialogs/mockup-viewer/mockup-viewer';
import { PlacementOverlay } from './components/placement-overlay/placement-overlay';
import { FeedbackList } from './components/feedback-list/feedback-list';
import { ScreenshotCapture } from './screenshotCapture';

@NgModule({
  declarations: [AppComponent, CaptureOptions],
  bootstrap: [AppComponent],
  // MatDialog/MatButton + animations are kept for the CaptureOptions dialog used
  // by ScreenshotCapture; everything else from the old widget was removed.
  // StatusChip is a standalone component from @brafton/feedback-components;
  // IdentityModal / CaptureScreenshot / PlacementOverlay are the widget's own
  // standalone dialog/overlay components.
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    MatDialogModule,
    MatButtonModule,
    StatusChip,
    DisregardReasonDialog,
    IdentityModal,
    CaptureScreenshot,
    NewFeedback,
    FeedbackDetail,
    MockupViewer,
    PlacementOverlay,
    FeedbackList,
  ],
  providers: [
    ScreenshotCapture,
    // Route the widget's uncaught errors to its own, isolated Sentry client (never
    // the host page's errors). See widget-sentry.ts.
    { provide: ErrorHandler, useValue: new WidgetSentryErrorHandler() },
    // feedbackApiAuthInterceptor attaches the stored JWT to @brafton/feedback-api
    // calls only (scoped by API_BASE_URL); requests to other hosts are untouched.
    // Cast: the lib's HttpInterceptorFn is typed against the WORKSPACE's copy of
    // @angular/common/http (the libs are consumed cross-repo from its `dist`), so it
    // is nominally distinct from the widget's copy — HttpRequest/HttpHandlerFn carry
    // private members, which makes them nominally rather than structurally typed.
    // Both repos are on Angular 22, so the two declarations are equivalent and the
    // cast is safe. Do NOT try to fix this with an "@angular/common/http" tsconfig
    // path (see tsconfig.json) — webpack honours those paths too and would bundle a
    // type-only .d.ts, leaving withXhr undefined at runtime. The real fix is to stop
    // resolving the libs out of the workspace tree (copy/link them into this repo's
    // node_modules, or adopt preserveSymlinks).
    provideHttpClient(withXhr(), withInterceptors([feedbackApiAuthInterceptor as unknown as HttpInterceptorFn])),
    provideFeedbackApi({ baseUrl: environment.apiBaseUrl }),
  ],
})
export class AppModule {}
